/**
 * Domain vocabulary shared by the API, the web app and the orchestrator.
 *
 * These are the words the whole product argues in. They are defined once,
 * here, because a mismatch between what the UI calls a basis and what the
 * database stores is not a cosmetic bug — it is a wrong valuation.
 */
export const ROUTES = ['r1_staff', 'r2_advisor', 'r3_client'];
export const PURPOSES = [
    'iht_estate',
    'cgt',
    'emi_csop',
    'ers_other',
    'commercial_sale',
    'dispute',
    'financial_reporting',
    'matrimonial',
    'other',
];
export const BASES = [
    's160_market_value',
    's272_market_value',
    'ifrs13_fair_value',
    'frs102_fair_value',
    'ipev_fair_value',
    'open_market',
    'other',
];
/** Provenance is carried by every fact in the system (doctrine 2). */
export const PROVENANCES = [
    'registry_confirmed',
    'data_derived',
    'management_asserted',
    'corroborated',
    'external_expert',
];
export const ROLES = [
    'owner',
    'preparer',
    'reviewer',
    'viewer',
    'acumon_reviewer',
    'acumon_admin',
];
export const CONTROL_ASSESSMENTS = [
    'control',
    'joint',
    'influential_minority',
    'small_minority',
];
/**
 * What an evidence point measures. The list is the database's own CHECK
 * constraint (0001, evidence.evidence_points) — kept here so the API and the
 * screen offer exactly what the column will accept, rather than discovering
 * the difference as a constraint violation in front of a valuer.
 */
export const EVIDENCE_METRICS = [
    'ebitda_multiple',
    'revenue_multiple',
    'pe',
    'property_yield',
    'cap_rate',
    'dlom_observation',
    'dloc_observation',
    'control_premium',
    'credit_spread',
    'volatility',
    'deal_multiple',
    'other',
];
/**
 * How good the evidence is, in descending order of weight. This is the
 * valuation equivalent of asking where a figure came from before asking what
 * it says: a completed transaction in the subject's own sector outranks a
 * published survey, which outranks a broker's view.
 */
export const EVIDENCE_QUALITIES = [
    'primary_transaction',
    'published_study',
    'broker_research',
    'computed_from_filings',
];
export const METHODS = [
    'earnings_multiple',
    'dcf',
    'nav',
    'hybrid',
    'recent_transaction_anchor',
    'opm_waterfall',
];
/**
 * What a valuation unit is a unit *of*. `whole_entity` is the unremarkable
 * case and exists in every engagement so the model stays uniform; the other
 * three are splits, and a split has to be justified (below).
 */
export const UNIT_TYPES = [
    'whole_entity',
    'business_unit',
    'activity_segment',
    'surplus_asset_pool',
];
/**
 * The unit-split rule (§5.2/§8). A business may be carved into units only
 * where the carve changes the valuation basis, the risk profile and therefore
 * the multiple, or whether the piece could be sold on its own. Anything else
 * is commentary about the business rather than a unit of it, and produces a
 * build-up nobody can defend line by line.
 *
 * The list is the database's own CHECK constraint (0001,
 * engagement.valuation_units) — kept here so the API and the screen offer
 * exactly what the column will accept.
 */
export const SPLIT_CRITERIA = [
    'basis_change',
    'risk_multiple_change',
    'standalone_saleability',
];
// ---------------------------------------------------------------------
// Human-facing labels. The UI never invents its own wording for a domain
// term; it reads it from here so the report, the API and the screen all
// say the same thing.
// ---------------------------------------------------------------------
export const PURPOSE_LABELS = {
    iht_estate: 'Inheritance tax — estate',
    cgt: 'Capital gains tax',
    emi_csop: 'EMI / CSOP share options',
    ers_other: 'Employment-related securities (other)',
    commercial_sale: 'Commercial sale',
    dispute: 'Dispute / litigation',
    financial_reporting: 'Financial reporting',
    matrimonial: 'Matrimonial',
    other: 'Other',
};
export const BASIS_LABELS = {
    s160_market_value: 'Market value — IHTA 1984 s.160',
    s272_market_value: 'Market value — TCGA 1992 s.272',
    ifrs13_fair_value: 'Fair value — IFRS 13',
    frs102_fair_value: 'Fair value — FRS 102',
    ipev_fair_value: 'Fair value — IPEV guidelines',
    open_market: 'Open market value',
    other: 'Other basis',
};
export const ROUTE_LABELS = {
    r1_staff: 'R1 — Acumon staff',
    r2_advisor: 'R2 — Professional advisor',
    r3_client: 'R3 — Direct client',
};
/**
 * Control is a judgement about what the holding can actually do, not a
 * percentage band: 40% of a company whose remaining shares are held by one
 * other person is a different thing from 40% against a wide register. The
 * labels name the judgement and leave the thresholds to the valuer.
 */
export const CONTROL_LABELS = {
    control: 'Control — can pass an ordinary resolution alone',
    joint: 'Joint control — control shared with another holder',
    influential_minority: 'Influential minority — can block, but not direct',
    small_minority: 'Small minority — no influence over the company',
};
export const EVIDENCE_METRIC_LABELS = {
    ebitda_multiple: 'EV/EBITDA multiple',
    revenue_multiple: 'EV/Revenue multiple',
    pe: 'Price/earnings ratio',
    property_yield: 'Property yield',
    cap_rate: 'Capitalisation rate',
    dlom_observation: 'Discount for lack of marketability — observed',
    dloc_observation: 'Discount for lack of control — observed',
    control_premium: 'Control premium',
    credit_spread: 'Credit spread',
    volatility: 'Volatility',
    deal_multiple: 'Completed deal multiple',
    other: 'Other observation',
};
export const EVIDENCE_QUALITY_LABELS = {
    primary_transaction: 'Primary transaction — a deal that completed',
    published_study: 'Published study — a survey or restricted-stock analysis',
    broker_research: 'Broker research — a market participant’s view',
    computed_from_filings: 'Computed from filings — derived from public accounts',
};
export const PROVENANCE_LABELS = {
    registry_confirmed: 'Registry confirmed',
    data_derived: 'Data derived',
    management_asserted: 'Management asserted',
    corroborated: 'Corroborated',
    external_expert: 'External expert',
};
/**
 * Provenance-aware report language (§14). A fact's provenance decides how
 * the narrative may state it — "we were informed by management that…"
 * is not a stylistic choice, it is the disclosure that keeps an asserted
 * fact from reading as a verified one.
 */
export const PROVENANCE_NARRATIVE = {
    registry_confirmed: 'confirmed by {source}',
    data_derived: 'derived from {source}',
    management_asserted: 'we were informed by management that',
    corroborated: 'management advised, and {source} independently supports, that',
    external_expert: 'as valued by {expert}, on whose report we rely',
};
/** Ranked weakest to strongest — the review gate surfaces the weak end first. */
export const PROVENANCE_STRENGTH = {
    management_asserted: 1,
    data_derived: 2,
    corroborated: 3,
    external_expert: 4,
    registry_confirmed: 5,
};
export const METHOD_LABELS = {
    earnings_multiple: 'Capitalised earnings',
    dcf: 'Discounted cash flow',
    nav: 'Net asset value',
    hybrid: 'Hybrid (trading + surplus assets)',
    recent_transaction_anchor: 'Recent transaction anchor',
    opm_waterfall: 'OPM waterfall',
};
export const UNIT_TYPE_LABELS = {
    whole_entity: 'The whole company',
    business_unit: 'A business unit',
    activity_segment: 'An activity segment',
    surplus_asset_pool: 'A pool of surplus assets',
};
export const SPLIT_CRITERION_LABELS = {
    basis_change: 'It is valued on a different basis',
    risk_multiple_change: 'It carries a different risk profile, so a different multiple',
    standalone_saleability: 'It could be sold on its own',
};
// ---------------------------------------------------------------------
// Purpose gates everything (doctrine 3)
// ---------------------------------------------------------------------
/**
 * Which bases a purpose permits. An IHT valuation on anything other than
 * the s.160 basis is not an IHT valuation, so this is a hard constraint
 * mirrored by a CHECK constraint in the database.
 */
export const PURPOSE_BASES = {
    iht_estate: ['s160_market_value'],
    cgt: ['s272_market_value'],
    emi_csop: ['s272_market_value', 'open_market'],
    ers_other: ['s272_market_value', 'open_market'],
    financial_reporting: ['ifrs13_fair_value', 'frs102_fair_value', 'ipev_fair_value'],
    commercial_sale: ['open_market', 'other'],
    dispute: ['open_market', 's272_market_value', 'other'],
    matrimonial: ['open_market', 'other'],
    other: [...BASES],
};
export function defaultBasisFor(purpose) {
    const allowed = PURPOSE_BASES[purpose];
    return allowed[0] ?? 'other';
}
export function isBasisValidFor(purpose, basis) {
    return PURPOSE_BASES[purpose].includes(basis);
}
/** Fiscal purposes must resolve to a value per holding at the top entity (§5.3). */
export const FISCAL_PURPOSES = ['iht_estate', 'cgt', 'emi_csop', 'ers_other'];
export function isFiscal(purpose) {
    return FISCAL_PURPOSES.includes(purpose);
}
/**
 * The reviewer role that must approve release, by route (§3).
 * R3 (direct client) always requires an Acumon reviewer — the client
 * cannot self-approve their own valuation out of the door.
 */
export function requiredReleaseRole(route) {
    return route === 'r3_client' ? 'acumon_reviewer' : 'reviewer';
}
// ---------------------------------------------------------------------
// Instruments (§5.5, §9.2)
// ---------------------------------------------------------------------
/**
 * The instrument classes. One list, mirrored by the CHECK constraint on
 * `engagement.instruments.class`, by the classification prompt's output
 * contract, and by the calculation engine's `instrument_type`. Four copies
 * of a vocabulary is three chances to mistranslate at the boundary where
 * it costs most.
 */
export const INSTRUMENT_CLASSES = [
    'ordinary_share',
    'pref_share',
    'growth_share',
    'loan_note',
    'convertible',
    'option',
    'warrant',
    'earn_out',
    'swap',
    'forward',
    'quoted_security',
    'crypto',
    'other',
];
export const INSTRUMENT_CLASS_LABELS = {
    ordinary_share: 'Ordinary share',
    pref_share: 'Preference share',
    growth_share: 'Growth share',
    loan_note: 'Loan note',
    convertible: 'Convertible loan note',
    option: 'Option',
    warrant: 'Warrant',
    earn_out: 'Earn-out',
    swap: 'Swap',
    forward: 'Forward',
    quoted_security: 'Quoted security',
    crypto: 'Crypto holding',
    other: 'Other',
};
/** The classes the calculation engine prices today. */
export const PRICED_INSTRUMENT_CLASSES = [
    'option',
    'warrant',
    'loan_note',
    'convertible',
    'pref_share',
];
export function isPriceable(cls) {
    return PRICED_INSTRUMENT_CLASSES.includes(cls);
}
/** IFRS 13's fair value hierarchy, which drives workflow rather than presentation. */
export const INSTRUMENT_TIERS = ['t1_quoted', 't2_observable', 't3_judgement'];
export const INSTRUMENT_TIER_LABELS = {
    t1_quoted: 'Level 1 — quoted price in an active market',
    t2_observable: 'Level 2 — significant inputs observable',
    t3_judgement: 'Level 3 — significant inputs unobservable',
};
/**
 * A Level 3 price rests on inputs nobody can look up, so it does not become
 * usable by being computed (§9.2). A reviewer has to have seen it.
 */
export function tierRequiresReviewer(tier) {
    return tier === 't3_judgement';
}
/**
 * How much scrutiny a tier asks for, as a number that can be compared.
 *
 * Used to tell a tier change that adds a control from one that removes it.
 * Moving an instrument up costs a reviewer's time; moving it down takes a
 * judgement input out of anybody's sight, and the two are not the same
 * edit even though they look alike in a dropdown.
 */
export const TIER_SCRUTINY = {
    t1_quoted: 1,
    t2_observable: 2,
    t3_judgement: 3,
};
/** Does moving from `from` to `to` take a control away? */
export function tierIsDowngrade(from, to) {
    return TIER_SCRUTINY[to] < TIER_SCRUTINY[from];
}
/**
 * Inputs that are evidence, not terms.
 *
 * The classification prompt forbids the model from supplying any of these:
 * they come from the evidence store and the curve data, are corridor-checked
 * separately, and are chosen by a person who has to justify them. Storing one
 * on the instrument as though it had been read off the paperwork would launder
 * a judgement into a fact, so they are supplied per pricing run instead and
 * the API refuses them in `terms`.
 */
export const INSTRUMENT_MARKET_INPUTS = [
    'volatility',
    'risk_free_rate',
    'dividend_yield',
    'discount_rate',
    'credit_spread',
    'underlying_price_per_share',
    /**
     * The proportion of passed dividends realistically expected to be received.
     * A judgement about a company that has already failed to pay once, which is
     * why it belongs here rather than in the terms: how much is outstanding and
     * where it ranks come off the accounts and the articles, but what will
     * actually be received is somebody's view, per valuation, with evidence.
     */
    'arrears_recoverability',
];
/**
 * What the paperwork has to say before a class can be priced at all, and
 * what the valuer has to supply from evidence. Named here rather than left
 * to the engine's 422 so that a half-read instrument can be shown as
 * incomplete on screen instead of being sent off to fail.
 */
export const INSTRUMENT_REQUIRED_TERMS = {
    option: ['expiry_date', 'exercise_price'],
    warrant: ['expiry_date', 'exercise_price', 'shares_in_issue', 'shares_under_instrument'],
    loan_note: ['issue_date', 'maturity_date', 'coupon_rate', 'nominal'],
    convertible: ['issue_date', 'maturity_date', 'coupon_rate', 'nominal', 'conversion_price'],
    pref_share: ['nominal', 'dividend_rate'],
};
export const INSTRUMENT_REQUIRED_MARKET_INPUTS = {
    option: ['underlying_price_per_share', 'volatility', 'risk_free_rate'],
    warrant: ['underlying_price_per_share', 'volatility', 'risk_free_rate'],
    loan_note: ['discount_rate'],
    convertible: ['underlying_price_per_share', 'volatility', 'risk_free_rate', 'credit_spread'],
    pref_share: ['discount_rate'],
};
/**
 * A redeemable preference share is a dated stream of payments and a
 * redemption, so it needs the dates a loan note needs. An irredeemable one
 * is a perpetuity and does not.
 */
/** The ways passed dividends can rank against the redemption money. */
export const ARREARS_RANKS = [
    'ahead_of_redemption',
    'with_redemption',
    'behind_redemption',
];
export const ARREARS_RANK_LABELS = {
    ahead_of_redemption: 'Ahead of the redemption',
    with_redemption: 'With the redemption',
    behind_redemption: 'Behind the redemption',
};
/**
 * The terms an arrears entitlement needs, once there is one.
 *
 * `arrears_amount` is what makes the rest apply: a class with nothing passed
 * needs none of them, and a class with something passed needs all of them,
 * because the engine will not guess when it is expected, where it ranks, or
 * under which article.
 */
export const ARREARS_TERMS = [
    'arrears_amount',
    'arrears_expected_payment_date',
    'arrears_ranks',
    'arrears_basis',
];
export function hasArrears(terms) {
    const amount = terms.arrears_amount;
    return amount !== undefined && amount !== null && amount !== '';
}
/**
 * What the valuer has to supply from evidence, for this class as recorded.
 *
 * Conditional because the arrears recoverability is only wanted where there
 * are arrears, and asking for it otherwise would be asking somebody to judge
 * the recoverability of nothing.
 */
export function requiredMarketInputsFor(cls, terms) {
    const base = INSTRUMENT_REQUIRED_MARKET_INPUTS[cls];
    if (cls === 'pref_share' && hasArrears(terms)) {
        return [...base, 'arrears_recoverability'];
    }
    return base;
}
export function requiredTermsFor(cls, terms) {
    if (cls === 'pref_share') {
        const dated = terms.redeemable !== false
            ? [...INSTRUMENT_REQUIRED_TERMS.pref_share, 'issue_date', 'maturity_date']
            : [...INSTRUMENT_REQUIRED_TERMS.pref_share];
        return hasArrears(terms) ? [...dated, ...ARREARS_TERMS] : dated;
    }
    return INSTRUMENT_REQUIRED_TERMS[cls];
}
export const MODULE_FLAGS = ['emi', 'pensions', 'instruments', 'sotp'];
/**
 * Has the licence said anything about modules at all?
 *
 * The distinction this makes is the whole reason the gate is safe to turn
 * on. "The licence says this tenant does not have instruments" and "the
 * licence has not mentioned instruments" look identical in a
 * `Record<ModuleFlag, boolean>` — both read as absent — and treating the
 * second as the first turns a commercial arrangement into an outage for
 * every tenant provisioned before the licensing service started sending the
 * field.
 *
 * So the flags are written all-or-nothing at sign-in: a claim that states
 * any module states all four, true or false. An empty set therefore means
 * "not stated", which is a different answer from "no".
 */
export function modulesAreLicensed(flags) {
    return MODULE_FLAGS.some((flag) => flag in flags);
}
/**
 * May this tenant use this module?
 *
 * Ungated where the licence is silent, and gated strictly the moment it is
 * not. That converts on its own as the licensing service starts sending the
 * field, with no deploy here and no tenant losing access on the way.
 */
export function moduleEnabled(flags, flag) {
    return modulesAreLicensed(flags) ? flags[flag] === true : true;
}
export const MODULE_LABELS = {
    emi: 'EMI / CSOP',
    pensions: 'DB pension accounting',
    instruments: 'Instruments library',
    sotp: 'Sum-of-the-parts',
};
//# sourceMappingURL=domain.js.map